// TAIR3-995: decide whether a failed profile load means "not logged in" or
// "the backend hiccuped, try again". Kept free of Vue/axios imports so it can
// be unit tested with plain node.

// Backoff between profile-load attempts within one load, in ms.
export const PROFILE_RETRY_DELAYS_MS = [1500, 4000];

/**
 * True when the error is a transient backend failure: no response at all
 * (network error, timeout) or a 5xx. A 4xx, or a response with no username,
 * means PAM looked and found nothing, which is a real logged-out state.
 */
export function isTransientAuthError(error) {
  if (!error) return false;
  const status = error.response && error.response.status;
  if (status === undefined) return true; // network error, timeout, CORS
  return status >= 500;
}

/** Error thrown by getAuthProfile when the load should be retried. */
export class TransientAuthError extends Error {
  constructor(cause) {
    super("Profile temporarily unavailable");
    this.name = "TransientAuthError";
    this.transient = true;
    this.cause = cause;
  }
}

// Fields a partial profile may omit, with the value to use when the previous
// state cannot be trusted to belong to the same account.
export const DECORATION_DEFAULTS = {
  firstname: "",
  lastname: "",
  isTairCurator: false,
  orcidId: null,
  affiliations: null,
};

/**
 * Turn a profile response into the state update to commit.
 *
 * A partial profile omits the fields of a failed decoration step. Those are
 * kept from `prevState` only when it belongs to the same account
 * (userIdentifier matches); otherwise they are reset to safe defaults so a
 * hiccup can never hand one user another user's curator flag, ORCID id or
 * affiliations.
 */
export function applyProfile(prevState, profile) {
  const update = { ...profile };
  delete update.partial;
  delete update.failedSteps;
  if (!profile.partial) return update;
  const sameUser =
    prevState &&
    prevState.userIdentifier != null &&
    String(prevState.userIdentifier) === String(profile.userIdentifier);
  for (const key of Object.keys(DECORATION_DEFAULTS)) {
    if (key in profile) continue;
    update[key] = sameUser ? prevState[key] : DECORATION_DEFAULTS[key];
  }
  return update;
}

// Background refresh of a failed/partial profile: start at the poller's own
// cadence and back off while the backend stays unhealthy.
export const REFRESH_MIN_MS = 5000;
export const REFRESH_MAX_MS = 60000;

/** Next delay after an unsuccessful background refresh. */
export function nextRefreshDelay(previousMs) {
  return Math.min(Math.max(previousMs, REFRESH_MIN_MS) * 2, REFRESH_MAX_MS);
}
